import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Flag, Users, Bell, PlusCircle, ChevronsRight, Mail } from 'lucide-react';
import { auth } from '../../config/firebase';
import { db } from '../../config/firestore';
import { collection, query, where, onSnapshot, doc, updateDoc, getDocs, addDoc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import InvitationsModal from '../groups/InvitationsModal';
import NotificationCenter from '../notifications/NotificationCenter';
import InvitationPopupModal from '../invitations/InvitationFloatingBar';
import { useToast } from '../../contexts/ToastContext';

function Dashboard() {
  const [displayName, setDisplayName] = useState('');
  const [groups, setGroups] = useState([]);
  const [isInvitationsModalOpen, setIsInvitationsModalOpen] = useState(false);
  const [hasPendingInvites, setHasPendingInvites] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationModalDismissed, setInvitationModalDismissed] = useState(false);
  const hasSeenInvites = useRef(false);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [previousMemberStatuses, setPreviousMemberStatuses] = useState({});

  // DEBUG LOGS
  console.log('🔍 Dashboard render - showInvitationModal:', showInvitationModal);
  console.log('🔍 Dashboard render - invitationModalDismissed:', invitationModalDismissed);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setDisplayName(user.displayName || '');
        
        // Set up real-time listener for groups
        const groupsQuery = query(
          collection(db, 'groups'),
          where(`members.${user.uid}`, '!=', null)
        );

        const groupsUnsubscribe = onSnapshot(groupsQuery, (snapshot) => {
          const groupsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .sort((a, b) => {
            // Sort by creation time (newest first)
            return b.created - a.created;
          });
          setGroups(groupsData);
        });

        // Set up real-time listener for pending invitations
        let invitesUnsubscribe = () => {};
        if (user.email) {
          const invitesQuery = query(
            collection(db, 'invitations'),
            where('email', '==', user.email),
            where('status', '==', 'pending')
          );
          
          invitesUnsubscribe = onSnapshot(invitesQuery, (snapshot) => {
            const hasPending = !snapshot.empty;
            setHasPendingInvites(hasPending);
            
            // Automatically open invitations modal if there are pending invites
            // and the user hasn't seen them yet
            if (hasPending && !hasSeenInvites.current) {
              setIsInvitationsModalOpen(true);
              hasSeenInvites.current = true;
            }
          });
        }
        
        // Set up listener for unread notification count
        const notificationsQuery = query(
          collection(db, 'notifications'),
          where('userId', '==', user.uid),
          where('read', '==', false)
        );
        
        const notificationsUnsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
          setNotificationCount(snapshot.docs.length);
        });
        
        // Make sure to clean up all listeners when component unmounts
        return () => {
          groupsUnsubscribe();
          invitesUnsubscribe();
          notificationsUnsubscribe();
        };
      }
    });

    return () => unsubscribe();
  }, []);

  // Check for member status changes when groups update
  useEffect(() => {
    if (groups.length > 0 && auth.currentUser) {
      const newMemberStatuses = {};
      let shouldUpdate = false;
      
      groups.forEach(group => {
        if (group.members) {
          Object.entries(group.members).forEach(([userId, memberData]) => {
            const key = `${group.id}-${userId}`;
            newMemberStatuses[key] = memberData.status;
            
            // If this is a known user who just became available (status changed from false to true)
            if (
              previousMemberStatuses[key] === false && 
              memberData.status === true && 
              userId !== auth.currentUser?.uid // Don't notify about your own status changes
            ) {
              // Show toast notification
              addToast(
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-2">
                    <span className="text-xs font-medium text-white">
                      {getInitials(memberData.displayName || 'User')}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">{memberData.displayName || 'User'}</span>
                    {' '}is now available in{' '}
                    <span className="font-medium">{group.name}</span>
                  </div>
                </div>,
                'success'
              );
              
              // Also create a database notification for the notification center
              const notification = {
                userId: auth.currentUser.uid,
                type: 'membership',
                action: 'status',
                read: false,
                createdAt: new Date(),
                message: `${memberData.displayName || 'User'} is now available in ${group.name}`,
                relatedGroupId: group.id,
                relatedGroupName: group.name,
                relatedUserId: userId,
                relatedUserName: memberData.displayName || 'User'
              };
              
              // Add to Firestore
              addDoc(collection(db, 'notifications'), notification)
                .then(() => console.log('Status notification created'))
                .catch(err => console.error('Error creating status notification:', err));
            }
            
            // Check if the status has changed
            if (previousMemberStatuses[key] !== memberData.status) {
              shouldUpdate = true;
            }
          });
        }
      });
      
      // Only update the state if something changed
      if (shouldUpdate || Object.keys(previousMemberStatuses).length !== Object.keys(newMemberStatuses).length) {
        setPreviousMemberStatuses(newMemberStatuses);
      }
    }
  }, [groups, addToast, auth.currentUser]);

  // Invitation modal visibility logic
  useEffect(() => {
    const checkInvitationModalVisibility = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        // Check if user has dismissed the modal recently
        const dismissedKey = `invitation_modal_dismissed_${user.uid}`;
        const dismissedTime = localStorage.getItem(dismissedKey);
        
        if (dismissedTime) {
          const dismissedDate = new Date(dismissedTime);
          const daysSinceDismissed = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
          
          // Show again after 7 days
          if (daysSinceDismissed < 7) {
            return;
          }
        }

        // Get user data to determine if they should see the invitation modal
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();

        // Show invitation modal logic:
        const shouldShow = (
          // New users (less than 30 days) OR
          (userData?.createdAt && (new Date() - userData.createdAt.toDate()) < 30 * 24 * 60 * 60 * 1000) ||
          
          // Users with active groups (more likely to invite) OR
          (groups.length > 0 && groups.some(g => Object.keys(g.members || {}).length > 1)) ||
          
          // Users who haven't sent many invitations yet
          (!userData?.invitationsSent || userData.invitationsSent < 3)
        ) && !invitationModalDismissed;

        setShowInvitationModal(shouldShow);

      } catch (error) {
        console.error('Error checking invitation modal visibility:', error);
      }
    };

    // Check visibility when groups change (indicates user activity)
    if (groups.length > 0) {
      checkInvitationModalVisibility();
    }
  }, [groups, invitationModalDismissed]);

  useEffect(() => {
    // Update the document title when notification count changes
    if (document.hidden && notificationCount > 0) {
      document.title = `(${notificationCount}) Semafor`;
    } else {
      document.title = 'Semafor';
    }
    
    // Reset title when the user returns to the tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        document.title = 'Semafor';
      } else if (notificationCount > 0) {
        document.title = `(${notificationCount}) Semafor`;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [notificationCount]);

  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleStatusChange = async (groupId, newStatus) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;
      
      await updateDoc(doc(db, 'groups', groupId), {
        [`members.${userId}.status`]: newStatus,
        [`members.${userId}.lastUpdated`]: new Date()
      });
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDismissInvitationModal = () => {
    setShowInvitationModal(false);
    setInvitationModalDismissed(true);
    
    // Store dismissal in localStorage
    const user = auth.currentUser;
    if (user) {
      const dismissedKey = `invitation_modal_dismissed_${user.uid}`;
      localStorage.setItem(dismissedKey, new Date().toISOString());
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Flag className="h-6 w-6 text-green-600" />
              <span className="text-xl font-semibold">Semafor</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative cursor-pointer" onClick={() => setIsInvitationsModalOpen(true)}>
                <Mail className="h-5 w-5 text-gray-600" />
                {hasPendingInvites && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                )}
              </div>
              <div className="relative cursor-pointer" onClick={() => setIsNotificationCenterOpen(true)}>
                <Bell className="h-5 w-5 text-gray-600" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </div>
              <div 
                className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center cursor-pointer"
                onClick={handleProfileClick}
              >
                <span className="text-sm font-medium text-white">
                  {getInitials(displayName)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Groups</h1>
          <div className="flex items-center space-x-2">

            <Button 
              onClick={() => navigate('/groups/create')} 
              className="flex items-center space-x-2"
            >
              <PlusCircle className="h-5 w-5" />
              <span>New Group</span>
            </Button>
          </div>
        </div>

        {/* Groups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.length === 0 ? (
            <div className="col-span-3 text-center py-8 text-gray-500">
              You haven't joined any groups yet. Create one to get started!
            </div>
          ) : (
            groups.map((group) => (
            <Card key={group.id} className="bg-white">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2 cursor-pointer group" onClick={() => navigate(`/groups/${group.id}`)}>
                    <span className="text-lg font-semibold">{group.name}</span>
                    <ChevronsRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </div>
                  <Switch 
                    checked={group.members?.[auth.currentUser?.uid]?.status || false}
                    onCheckedChange={(checked) => handleStatusChange(group.id, checked)}
                    className="data-[state=checked]:bg-green-500"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Users className="h-4 w-4" />
                    <span>{Object.keys(group.members || {}).length} members</span>
                  </div>
                  <div>
                    <span className="text-green-600">
                      {Object.values(group.members || {}).filter(m => m.status).length} available
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            ))
          )}
        </div>
      </div>

      {/* Invitation Popup Modal */}
      {showInvitationModal && (
        <InvitationPopupModal onDismiss={handleDismissInvitationModal} />
      )}

      <InvitationsModal 
        isOpen={isInvitationsModalOpen}
        onClose={() => {
          setIsInvitationsModalOpen(false);
          // Reset the seen flag when modal is closed
          hasSeenInvites.current = false;
        }}
      />
      
      <NotificationCenter
        isOpen={isNotificationCenterOpen}
        onClose={() => setIsNotificationCenterOpen(false)}
      />
    </div>
  );
}

export default Dashboard;
